import { z } from 'zod';
import { defineListQuery, inForceFilter, listFilter } from './list-query.schemas';

/**
 * Pre-trip check templates and submissions (gitaumoses4/chukua#71). A template is a truck class's inspection list; an edit is a new
 * version, and a submitted check names the version it ran against.
 */

export const checkTemplateItemInputSchema = z
  .object({
    area: z.string().trim().min(1).max(60),
    label: z.string().trim().min(1).max(120),
    /** A failed critical item stops the trip and takes the truck off the road; any other failed item raises a work order and lets the trip go. */
    isCritical: z.boolean().default(false),
  })
  .strict();
export type CheckTemplateItemInput = z.infer<typeof checkTemplateItemInputSchema>;

const templateItemsSchema = z.array(checkTemplateItemInputSchema).min(1).max(100);

/** `POST /check-templates` — one current template per truck class code. */
export const createCheckTemplateInputSchema = z
  .object({ truckClassCode: z.string().trim().min(1).max(60), name: z.string().trim().min(1).max(120), items: templateItemsSchema })
  .strict();
export type CreateCheckTemplateInput = z.infer<typeof createCheckTemplateInputSchema>;

/** `PUT /check-templates/:id` — closes that version and inserts the next with these items. */
export const updateCheckTemplateInputSchema = z.object({ name: z.string().trim().min(1).max(120), items: templateItemsSchema }).strict();
export type UpdateCheckTemplateInput = z.infer<typeof updateCheckTemplateInputSchema>;

export const checkTemplateListQuerySchema = defineListQuery({
  sortFields: ['truckClassCode', 'name', 'version', 'validFrom'],
  defaultSort: { sort: 'truckClassCode', order: 'asc' },
  filters: { truckClassCode: listFilter(z.string().trim().min(1).max(60)), current: inForceFilter },
});
export type CheckTemplateListQuery = z.infer<typeof checkTemplateListQuerySchema>;

export interface CheckTemplateItemView {
  id: string;
  position: number;
  area: string;
  label: string;
  isCritical: boolean;
}

export interface CheckTemplateView {
  id: string;
  truckClassCode: string;
  name: string;
  version: number;
  validFrom: string;
  /** Null while this is the class's current template. */
  validTo: string | null;
  items: CheckTemplateItemView[];
}

export type CheckTemplateListItemView = Omit<CheckTemplateView, 'items'> & { itemCount: number };

export const MAX_CHECK_PHOTOS_PER_ITEM = 5;

export const checkItemResultSchema = z.object({
  itemId: z.uuid(),
  passed: z.boolean(),
  note: z.string().trim().max(500).optional(),
  /** Client media ids of the photos queued through the photo pipeline (`trip_media.client_media_id`). */
  photoIds: z.array(z.string().min(1).max(200)).max(MAX_CHECK_PHOTOS_PER_ITEM).default([]),
});
export type CheckItemResult = z.infer<typeof checkItemResultSchema>;

/**
 * The payload of a `pre_trip_checked` event. A failed item needs a photo and a note: a failure nobody can look at is not evidence. The same
 * rule is on the phone (`CheckSubmissionPolicy`), so the server never sees the common case.
 */
export const preTripCheckPayloadSchema = z.object({
  templateId: z.uuid(),
  odometerKm: z.number().int().min(0).max(9_999_999),
  fuelPercent: z.number().int().min(0).max(100),
  results: z
    .array(checkItemResultSchema)
    .min(1)
    .max(100)
    .superRefine((results, ctx) => {
      results.forEach((result, index) => {
        if (result.passed) return;
        if (!result.note) ctx.addIssue({ code: 'custom', path: [index, 'note'], message: 'A failed item needs a note' });
        if (result.photoIds.length === 0) ctx.addIssue({ code: 'custom', path: [index, 'photoIds'], message: 'A failed item needs a photo' });
      });
    }),
});
export type PreTripCheckPayload = z.infer<typeof preTripCheckPayloadSchema>;

export const VEHICLE_CHECK_OUTCOMES = ['passed', 'failed', 'critical_failed'] as const;
export type VehicleCheckOutcome = (typeof VEHICLE_CHECK_OUTCOMES)[number];

/** What the phone is given with the current trip: the template to run, or none (then the check is not offered). */
export const syncCheckTemplateSchema = z.object({
  templateId: z.uuid(),
  name: z.string(),
  version: z.number().int(),
  items: z.array(z.object({ id: z.uuid(), area: z.string(), label: z.string(), isCritical: z.boolean() })),
});
export type SyncCheckTemplate = z.infer<typeof syncCheckTemplateSchema>;
